import React, { useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Modal, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  dockerAction,
  dockerImageSaveUrl,
  dockerList,
  dockerListImages,
  dockerLogs,
  dockerPruneImages,
  dockerRemoveImage,
  fetchProjectServers,
  getProjects,
} from "../../redux/actionCreator";
import { project_data } from "../../redux/commonReducer";
import { Container, Sparkles, X, Settings, RotateCw, Square, Play, FileText, Download, HardDrive } from "lucide-react";
import DiskReclaimModal from "./DiskReclaimModal";
import "./dockerPanel.scss";
import "./dockerPage.scss";

const STATE_BADGE = {
  running: "success",
  paused: "warning",
  restarting: "info",
  exited: "secondary",
  dead: "danger",
  created: "secondary",
  removing: "danger",
};
const shortId = (id) => (id ? String(id).slice(0, 12) : "");

/**
 * Standalone Docker management page (sidebar entry). Pick a project + server,
 * then manage that server's containers. Replaces the per-server Docker modal
 * as the primary entry point while reusing the same backend actions.
 */
export default function DockerPage() {
  const dispatch = useDispatch();
  const projects = useSelector(project_data) || [];

  const [projectId, setProjectId] = useState("");
  const [servers, setServers] = useState([]);
  const [serverId, setServerId] = useState("");
  const [loadingServers, setLoadingServers] = useState(false);

  const [rows, setRows] = useState([]);
  const [images, setImages] = useState([]);
  const [view, setView] = useState("containers"); // containers | images
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [logs, setLogs] = useState(null);
  const [imgBusy, setImgBusy] = useState(null);
  const [diskOpen, setDiskOpen] = useState(false);

  // Load the project list once. Large page size so the dropdown isn't paged.
  useEffect(() => {
    dispatch(getProjects({ page: 0, limit: 200 }));
  }, [dispatch]);

  const selectedServer = useMemo(
    () => servers.find((s) => String(s._id) === String(serverId)) || null,
    [servers, serverId]
  );

  // Fetch servers when the project changes.
  useEffect(() => {
    setServers([]);
    setServerId("");
    setRows([]);
    setErr(null);
    if (!projectId) return;
    setLoadingServers(true);
    dispatch(fetchProjectServers(projectId)).then((res) => {
      setLoadingServers(false);
      if (res?.status) setServers(res.data || []);
    });
  }, [dispatch, projectId]);

  const load = async () => {
    if (!projectId || !serverId) return;
    setLoading(true);
    setErr(null);
    const res = await dispatch(dockerList(projectId, serverId));
    setLoading(false);
    if (res?.status) setRows(res.data || []);
    else { setRows([]); setErr(res?.message || "Failed to list containers"); }
  };

  const loadImages = async () => {
    if (!projectId || !serverId) return;
    setLoading(true);
    setErr(null);
    const res = await dispatch(dockerListImages(projectId, serverId));
    setLoading(false);
    if (res?.status) setImages(res.data || []);
    else { setImages([]); setErr(res?.message || "Failed to list images"); }
  };

  const refresh = () => (view === "containers" ? load() : loadImages());

  // Auto-load when a server is picked or the view switches.
  useEffect(() => {
    if (serverId) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverId, view]);

  const removeImage = async (img) => {
    const ref = img.ID || `${img.Repository}:${img.Tag}`;
    if (!window.confirm(`Remove image ${img.Repository}:${img.Tag}?`)) return;
    setImgBusy(ref);
    let res = await dispatch(dockerRemoveImage(projectId, serverId, img.ID));
    // If it's in use / has children, offer a force removal.
    if (!res?.status && window.confirm(`${res?.message || "Remove failed"}\n\nForce remove (-f)?`)) {
      res = await dispatch(dockerRemoveImage(projectId, serverId, img.ID, true));
    }
    setImgBusy(null);
    if (res?.status) loadImages();
    else if (res?.message) alert(res.message);
  };

  const pruneImages = async () => {
    if (!window.confirm("Remove all dangling images (docker image prune -f)?")) return;
    const res = await dispatch(dockerPruneImages(projectId, serverId, false));
    if (res?.status) { loadImages(); if (res.data) alert(res.data); }
    else alert(res?.message || "Prune failed");
  };

  // Download a `docker save <ref>` tarball. We don't fetch with axios —
  // images are often hundreds of megabytes, which would buffer in JS
  // memory and crash the tab. Instead we click a transient <a> with
  // the streaming endpoint URL; the browser handles the download
  // natively and the urltoken cookie auth-ing it travels automatically.
  const downloadImage = (img, opts = {}) => {
    const ref = (img.Repository && img.Tag && img.Tag !== "<none>")
      ? `${img.Repository}:${img.Tag}`
      : img.ID;
    if (!ref) { alert("Image has no usable ref to save"); return; }
    if (!window.confirm(`Download "${ref}" as ${opts.gzip ? ".tar.gz" : ".tar"}?\n\nLarge images (multi-GB) may take a while and consume disk space temporarily on the remote server.`)) return;
    const url = dockerImageSaveUrl(projectId, serverId, ref, { gzip: !!opts.gzip });
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    // download="" lets the server's Content-Disposition pick the name.
    a.setAttribute("download", "");
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const act = (cid, action) =>
    dispatch(dockerAction(projectId, serverId, cid, action, (ok) => ok && load()));

  const openLogs = async (cid, name) => {
    setLogs({ cid, name, tail: 500, log: "", loading: true });
    const res = await dispatch(dockerLogs(projectId, serverId, cid, 500));
    setLogs({
      cid, name, tail: 500,
      log: res?.status ? res.data?.log || "" : res?.message || "(no logs)",
      loading: false,
    });
  };

  const openTerminal = (cid) => {
    if (!selectedServer) return;
    const qs = new URLSearchParams({
      label: `${selectedServer.label} · ${cid.slice(0, 12)}`,
      host: `${selectedServer.username}@${selectedServer.host}:${selectedServer.port}`,
      container: cid,
    });
    window.open(`/terminal/${projectId}/${serverId}?${qs.toString()}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="docker-page">
      <div className="docker-page-head">
        <h2 className="m-0 d-inline-flex align-items-center gap-2"><Container size={22} /> Docker</h2>
        <div className="docker-selectors">
          <select
            className="form-select form-select-sm"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          >
            <option value="">— Select project —</option>
            {projects.map((p) => (
              <option key={p._id} value={p._id}>{p.project_name || p.name}</option>
            ))}
          </select>
          <select
            className="form-select form-select-sm"
            value={serverId}
            onChange={(e) => setServerId(e.target.value)}
            disabled={!projectId || loadingServers}
          >
            <option value="">
              {loadingServers ? "Loading servers…" : "— Select server —"}
            </option>
            {servers.map((s) => (
              <option key={s._id} value={s._id}>
                {s.label} ({s.username}@{s.host})
              </option>
            ))}
          </select>
          {serverId && (
            <div className="btn-group btn-group-sm" role="group">
              <Button size="sm" variant={view === "containers" ? "primary" : "light"} onClick={() => setView("containers")}>Containers</Button>
              <Button size="sm" variant={view === "images" ? "primary" : "light"} onClick={() => setView("images")}>Images</Button>
            </div>
          )}
          <Button size="sm" variant="light" onClick={refresh} disabled={!serverId || loading}>
            {loading ? <Spinner size="sm" animation="border" /> : "↻ Refresh"}
          </Button>
          {view === "images" && serverId && (
            <Button size="sm" variant="light" onClick={pruneImages} title="Remove dangling images"><Sparkles size={14} className="me-1" /> Prune</Button>
          )}
          {serverId && (
            <Button
              size="sm" variant="light"
              onClick={() => setDiskOpen(true)}
              title="See what is reclaimable and free it selectively"
            >
              <HardDrive size={14} className="me-1" /> Disk
            </Button>
          )}
        </div>
      </div>

      {!serverId ? (
        <div className="docker-empty">
          <div className="empty-art"><Container size={32} strokeWidth={1.2} /></div>
          <h4>Pick a project &amp; server</h4>
          <p>Select a server above to view and manage its Docker containers.</p>
        </div>
      ) : (
        <>
          {selectedServer && (
            <div className="docker-server-meta">
              <code>{selectedServer.username}@{selectedServer.host}:{selectedServer.port}</code>
              <span className="text-muted ms-auto small">
                {view === "containers"
                  ? `${rows.length} container${rows.length === 1 ? "" : "s"}`
                  : `${images.length} image${images.length === 1 ? "" : "s"}`}
              </span>
            </div>
          )}
          {err && <div className="alert alert-danger">{err}</div>}

          {view === "images" && (
            <div className="docker-table-wrap">
              <table className="docker-table">
                <thead>
                  <tr>
                    <th>Repository</th>
                    <th>Tag</th>
                    <th>Image ID</th>
                    <th>Size</th>
                    <th>Created</th>
                    <th className="text-end" style={{ width: 1 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} className="empty"><Spinner size="sm" animation="border" /> Loading…</td></tr>
                  ) : images.length === 0 ? (
                    <tr><td colSpan={6} className="empty">No images on this server.</td></tr>
                  ) : (
                    images.map((img) => (
                      <tr key={img.ID + img.Repository + img.Tag}>
                        <td><code className="small">{img.Repository}</code></td>
                        <td><span className="badge bg-secondary">{img.Tag}</span></td>
                        <td><code className="small">{(img.ID || "").replace(/^sha256:/, "").slice(0, 12)}</code></td>
                        <td className="small">{img.Size}</td>
                        <td className="small">{img.CreatedSince || img.CreatedAt}</td>
                        <td className="text-end">
                          <Dropdown align="end" className="d-inline me-2">
                            <Dropdown.Toggle as={Button} size="sm" variant="outline-primary">
                              <Download size={12} className="me-1" /> Save
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => downloadImage(img)}>
                                <Download size={12} className="me-2" /> Download .tar
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => downloadImage(img, { gzip: true })}>
                                <Download size={12} className="me-2" /> Download .tar.gz
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                          <Button size="sm" variant="outline-danger" disabled={imgBusy === img.ID}
                            onClick={() => removeImage(img)}>
                            {imgBusy === img.ID ? <Spinner size="sm" animation="border" /> : <span className="d-inline-flex align-items-center gap-1"><X size={12} /> Remove</span>}
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {view === "containers" && (
          <div className="docker-table-wrap">
            <table className="docker-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Image</th>
                  <th>State</th>
                  <th>Status</th>
                  <th>Ports</th>
                  <th className="text-end" style={{ width: 1 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="empty"><Spinner size="sm" animation="border" /> Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="empty">
                      <div className="empty-state">
                        <div className="empty-art"><Container size={32} strokeWidth={1.2} /></div>
                        <h4>No containers</h4>
                        <p>Either docker isn't installed on this host, or the SSH user can't run <code>docker</code>. Use <strong className="d-inline-flex align-items-center gap-1"><Settings size={12} /> Setup</strong> to install it.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows.map((c) => {
                    const cid = c.ID || c.Id || c.ContainerID;
                    const name = c.Names || c.Name || shortId(cid);
                    const state = (c.State || "").toLowerCase();
                    const isRunning = state === "running";
                    return (
                      <tr key={cid}>
                        <td>
                          <div className="emp-name">{name}</div>
                          <div className="emp-id"><code>{shortId(cid)}</code></div>
                        </td>
                        <td><code className="small">{c.Image}</code></td>
                        <td><span className={`badge bg-${STATE_BADGE[state] || "secondary"}`}>{state || "—"}</span></td>
                        <td className="small">{c.Status}</td>
                        <td className="small"><code>{c.Ports || ""}</code></td>
                        <td className="text-end">
                          {isRunning ? (
                            <>
                              <Button size="sm" variant="outline-warning" className="me-1" aria-label="Restart" onClick={() => act(cid, "restart")}><RotateCw size={12} /></Button>
                              <Button size="sm" variant="outline-danger" className="me-1" onClick={() => act(cid, "stop")}><Square size={12} fill="currentColor" className="me-1" /> Stop</Button>
                            </>
                          ) : (
                            <Button size="sm" variant="outline-success" className="me-1" onClick={() => act(cid, "start")}><Play size={12} className="me-1" /> Start</Button>
                          )}
                          <Dropdown align="end" className="d-inline">
                            <Dropdown.Toggle size="sm" variant="light" className="row-actions-btn">⋯</Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => openLogs(cid, name)}><FileText size={12} className="me-1" /> Logs</Dropdown.Item>
                              <Dropdown.Item onClick={() => openTerminal(cid)} disabled={!isRunning} title={isRunning ? "Open shell inside the container" : "Container must be running"}>
                                <Play size={12} className="me-1" /> Terminal (inside)
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          )}
        </>
      )}

      {logs && (
        <Modal show onHide={() => setLogs(null)} size="xl" centered>
          <Modal.Header closeButton>
            <h5 className="m-0">
              Logs — <code>{logs.name}</code>{" "}
              <small className="text-muted">last {logs.tail} lines</small>
            </h5>
          </Modal.Header>
          <Modal.Body className="p-0">
            {logs.loading ? (
              <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : (
              <pre className="docker-logs">{logs.log || "(empty)"}</pre>
            )}
          </Modal.Body>
        </Modal>
      )}

      {diskOpen && serverId && (
        <DiskReclaimModal
          projectId={projectId}
          serverId={serverId}
          serverLabel={servers.find((s) => String(s._id) === String(serverId))?.label || "server"}
          onClose={() => setDiskOpen(false)}
        />
      )}
    </div>
  );
}
